import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { StatType } from '../../services/statTypesService';
import { statTypesService } from '../../services';
import Accordion from '../shared/Accordion';
import Tooltip from '../shared/Tooltip';
import TableScrollTopBar from '../shared/TableScrollTopBar';
import LoadingSpinner from '../../../../shared/components/ui/LoadingSpinner';

interface StatTypesTableProps {
  searchQuery: string;
  refreshTrigger?: number;
  onEdit?: (entity: StatType) => void;
}

interface TableFilters {
  category: string;
  event_result: string;
  is_active: string;
}

const SCHEMA = [
  { name: 'emoji', type: 'varchar', nullable: true, width: 50 },
  { name: 'stat_key', type: 'varchar', nullable: false, width: 180 },
  { name: 'display_name', type: 'varchar', nullable: false, width: 180 },
  { name: 'category', type: 'varchar', nullable: false, width: 120 },
  { name: 'event_result', type: 'varchar', nullable: false, width: 100 },
  { name: 'short_description', type: 'text', nullable: true, width: 200 },
  { name: 'is_active', type: 'boolean', nullable: true, width: 80 },
  { name: 'is_calculated', type: 'boolean', nullable: true, width: 90 },
  { name: 'visible_in_reports', type: 'boolean', nullable: true, width: 100 },
  { name: 'top_stats', type: 'boolean', nullable: true, width: 80 },
  { name: 'sort_order', type: 'integer', nullable: true, width: 90 },
  { name: 'created_at', type: 'timestamp', nullable: true, width: 120 },
];

const StatTypesTable: React.FC<StatTypesTableProps> = ({
  searchQuery,
  refreshTrigger,
  onEdit,
}) => {
  const [data, setData] = useState<StatType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState<TableFilters>({
    category: 'all',
    event_result: 'all',
    is_active: 'all',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    recordsPerPage: 100,
    totalRecords: 0,
    totalPages: 0,
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const list = await statTypesService.getAll();
      setData(list);
    } catch (err) {
      console.error('Error fetching stat types:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchData();
    }
  }, [refreshTrigger, fetchData]);

  const filteredData = useMemo(() => {
    let result = data;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (row) =>
          row.stat_key?.toLowerCase().includes(q) ||
          row.display_name?.toLowerCase().includes(q) ||
          row.short_description?.toLowerCase().includes(q) ||
          row.category?.toLowerCase().includes(q) ||
          row.emoji?.includes(q)
      );
    }
    if (filters.category !== 'all') {
      result = result.filter((row) => row.category === filters.category);
    }
    if (filters.event_result !== 'all') {
      result = result.filter((row) => row.event_result === filters.event_result);
    }
    if (filters.is_active !== 'all') {
      result = result.filter((row) => String(row.is_active) === filters.is_active);
    }

    return result;
  }, [data, searchQuery, filters]);

  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key as keyof StatType];
      const bVal = b[sortConfig.key as keyof StatType];
      const mult = sortConfig.direction === 'asc' ? 1 : -1;
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return mult;
      if (bVal == null) return -mult;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return mult * aVal.localeCompare(bVal);
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return mult * (aVal - bVal);
      }
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        return mult * (aVal === bVal ? 0 : aVal ? 1 : -1);
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const start = (pagination.currentPage - 1) * pagination.recordsPerPage;
    return sortedData.slice(start, start + pagination.recordsPerPage);
  }, [sortedData, pagination.currentPage, pagination.recordsPerPage]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      totalRecords: filteredData.length,
      totalPages: Math.ceil(filteredData.length / prev.recordsPerPage) || 1,
      currentPage: 1,
    }));
  }, [filteredData]);

  const handleSort = (key: string) => {
    setSortConfig((curr) =>
      curr?.key === key
        ? { key, direction: curr.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const formatCell = (value: unknown, columnName: string): string => {
    if (value === null || value === undefined) return '';
    if (columnName === 'created_at') return new Date(value as string).toLocaleString();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const uniqueCategories = useMemo(() => {
    const set = new Set(data.map((r) => r.category).filter(Boolean));
    return Array.from(set).sort();
  }, [data]);

  const uniqueEventResults = useMemo(() => {
    const set = new Set(data.map((r) => r.event_result).filter(Boolean));
    return Array.from(set).sort();
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-4 overflow-visible">
      <Accordion title="Table Schema" defaultOpen={false}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-t border-b border-gray-200">
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Column</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Type</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 bg-gray-100">Nullable</th>
              </tr>
            </thead>
            <tbody>
              {SCHEMA.map((col) => (
                <tr key={col.name} className="border-b border-gray-200">
                  <td className="px-3 py-2 text-sm text-gray-900">{col.name}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{col.type}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{col.nullable ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Accordion>

      <Accordion title="Table Data" defaultOpen={true}>
        <div className="space-y-4 overflow-visible">
          <div className="flex flex-wrap items-center gap-2 p-4 bg-gray-50 rounded-lg">
            <select
              value={filters.category}
              onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Category: All</option>
              {uniqueCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select
              value={filters.event_result}
              onChange={(e) => setFilters((p) => ({ ...p, event_result: e.target.value }))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Event result: All</option>
              {uniqueEventResults.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <select
              value={filters.is_active}
              onChange={(e) => setFilters((p) => ({ ...p, is_active: e.target.value }))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Active: All</option>
              <option value="true">Active: Yes</option>
              <option value="false">Active: No</option>
            </select>
            <button
              onClick={() => setFilters({ category: 'all', event_result: 'all', is_active: 'all' })}
              className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Clear filters
            </button>
          </div>

          <TableScrollTopBar>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10 border-t border-b border-gray-200">
                <tr>
                  {SCHEMA.map((col) => (
                    <th
                      key={col.name}
                      className="px-3 py-3 text-left text-sm font-bold text-gray-900 cursor-pointer hover:bg-gray-100 border-r border-gray-200 bg-gray-100"
                      onClick={() => handleSort(col.name)}
                      style={{ width: col.width }}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{col.name}</span>
                        {sortConfig?.key === col.name && (
                          <span className="text-blue-500">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 w-20 bg-gray-100">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={SCHEMA.length + 1} className="px-6 py-12 text-center text-gray-500">
                      No stat types match your filters.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row) => (
                    <tr key={row.id} className="border-b border-gray-200 hover:bg-gray-50">
                      {SCHEMA.map((col) => (
                        <td key={col.name} className="px-3 py-2 text-sm text-gray-900">
                          <Tooltip content={formatCell(row[col.name as keyof StatType], col.name)}>
                            <div className="truncate" style={{ maxWidth: col.width }}>
                              {formatCell(row[col.name as keyof StatType], col.name)}
                            </div>
                          </Tooltip>
                        </td>
                      ))}
                      <td className="px-3 py-2">
                        <button
                          onClick={() => onEdit?.(row)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableScrollTopBar>

          <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Rows per page:</span>
              <select
                value={pagination.recordsPerPage}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setPagination((p) => ({
                    ...p,
                    recordsPerPage: v,
                    totalPages: Math.ceil(p.totalRecords / v) || 1,
                    currentPage: 1,
                  }));
                }}
                className="px-2 py-1 text-sm border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={100}>100</option>
                <option value={1000}>1000</option>
              </select>
              <span className="text-sm text-gray-700">
                Showing {(pagination.currentPage - 1) * pagination.recordsPerPage + 1} to{' '}
                {Math.min(pagination.currentPage * pagination.recordsPerPage, pagination.totalRecords)} of {pagination.totalRecords}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPagination((p) => ({ ...p, currentPage: p.currentPage - 1 }))}
                disabled={pagination.currentPage <= 1}
                className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed border-gray-300 hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination((p) => ({ ...p, currentPage: p.currentPage + 1 }))}
                disabled={pagination.currentPage >= pagination.totalPages}
                className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed border-gray-300 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </Accordion>
    </div>
  );
};

export default StatTypesTable;
